/**
 * How a `data-action` name finds its handler.
 *
 * A view does not know a client id or a user id at render time in any other
 * place than the element it is rendering, so an action name carries its
 * parameters: the connect screen emits `connect:revoke:<clientId>` and the
 * handler is registered as `connect:revoke`. Resolution is therefore a
 * longest-prefix match on `:` boundaries.
 *
 * It was an exact match, and that is a defect this module exists to make
 * impossible to reintroduce silently. With an exact match every parameterised
 * control in the application was inert: the lookup returned `undefined`, the
 * handler was never called, and the control still looked live. That included
 * **revoking a client grant** on the connect screen, removing a member on the
 * admin screen, and all three proposal decisions on the improvements queue:
 * security-relevant controls that a person would reasonably believe had taken
 * effect.
 */

/** A handler receives the element that was activated. */
export type ActionHandler = (element: HTMLElement) => void | Promise<void>;

export type ActionRegistry = Readonly<Record<string, ActionHandler>>;

/**
 * Resolve one action name to a handler, or `undefined` when nothing claims it.
 *
 * Longest prefix wins rather than first match, so a name registered as
 * `admin:role` is not shadowed by a shorter prefix that also happens to match
 * `admin:role:<userId>:<role>`.
 */
export function resolveAction(
  handlers: ActionRegistry,
  name: string,
): ActionHandler | undefined {
  const exact = handlers[name];
  if (exact !== undefined) return exact;

  let best: ActionHandler | undefined;
  let bestLength = -1;
  for (const [key, handler] of Object.entries(handlers)) {
    if (key.length > bestLength && name.startsWith(`${key}:`)) {
      best = handler;
      bestLength = key.length;
    }
  }
  return best;
}
